import { fs } from '@/theme/tokens';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import type {
  ImportStatus, MappingStatus, RunStatus, SourceRunStatus,
} from '@/api/hrms-sync';

export function SourceStatusIcon({ status }: { status: SourceRunStatus | undefined }) {
  const sx = { fontSize: fs.xl };
  switch (status) {
    case 'OK':
      return <CheckCircleIcon color="success" sx={sx} />;
    case 'PARTIAL':
      return <WarningAmberIcon color="warning" sx={sx} />;
    case 'FAILED':
      return <ErrorOutlineIcon color="error" sx={sx} />;
    case 'SKIPPED':
      return <RemoveCircleOutlineIcon color="disabled" sx={sx} />;
    case 'RUNNING':
      return <CircularProgress size={15} />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" sx={sx} />;
  }
}

const RUN_COLOR: Record<RunStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  QUEUED: 'info',
  RUNNING: 'info',
  CANCELLING: 'warning',
  COMPLETED: 'success',
  COMPLETED_WITH_ERRORS: 'warning',
  FAILED: 'error',
  CANCELLED: 'default',
};

const RUN_LABEL: Record<RunStatus, string> = {
  QUEUED: 'Queued',
  RUNNING: 'Running',
  CANCELLING: 'Stopping',
  COMPLETED: 'Completed',
  COMPLETED_WITH_ERRORS: 'Completed with errors',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

export function RunStatusChip({ status }: { status: RunStatus }) {
  return (
    <Chip size="small" color={RUN_COLOR[status] ?? 'default'}
      label={RUN_LABEL[status] ?? status} sx={{ fontWeight: 600 }} />
  );
}

const MAPPING: Record<MappingStatus, { label: string; color: 'default' | 'success' | 'info' | 'warning'; tip: string }> = {
  none: { label: 'Store only', color: 'default', tip: 'Stored in its own table; not imported into a payroll master.' },
  confirmed: { label: 'Confirmed', color: 'success', tip: 'Imported into payroll on every sync using the confirmed mapping.' },
  auto_ready: { label: 'Auto-matched', color: 'info', tip: 'Every required column matched with high confidence. Confirm it to import on dashboard syncs.' },
  needs_review: { label: 'Needs mapping', color: 'warning', tip: 'Some required columns could not be matched. Map them to import this data.' },
};

export function MappingChip({ status }: { status: MappingStatus }) {
  const m = MAPPING[status];
  return (
    <Tooltip title={m.tip}>
      <Chip size="small" variant={status === 'none' ? 'outlined' : 'filled'}
        color={m.color} label={m.label} />
    </Tooltip>
  );
}

const IMPORT: Record<ImportStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  IMPORTED: { label: 'Imported', color: 'success' },
  STORED_ONLY: { label: 'Stored', color: 'default' },
  NEEDS_MAPPING: { label: 'Needs mapping', color: 'warning' },
  BLOCKED: { label: 'Blocked', color: 'warning' },
  FAILED: { label: 'Import failed', color: 'error' },
  SKIPPED: { label: 'Skipped', color: 'default' },
};

export function ImportChip({ status }: { status: ImportStatus | undefined }) {
  if (!status) return null;
  const m = IMPORT[status] ?? { label: status, color: 'default' as const };
  return <Chip size="small" variant="outlined" color={m.color} label={m.label} />;
}

export function ConfidenceChip({ value }: { value: 'high' | 'medium' | 'low' | null }) {
  if (!value) return null;
  const color = value === 'high' ? 'success' : value === 'medium' ? 'warning' : 'error';
  return <Chip size="small" variant="outlined" color={color} label={value} sx={{ height: 20 }} />;
}

/**
 * Filter box over a column-mapping table: narrows the rows to the fields
 * whose name, note or mapped column matches. Purely a view filter — the
 * draft keeps every row, so a hidden row is still saved.
 */
export function FieldSearchBox({ value, onChange, shown, total }: {
  value: string;
  onChange: (v: string) => void;
  shown: number;
  total: number;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
      <TextField
        size="small" value={value} placeholder="Search fields"
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': 'Search fields', maxLength: 100 }}
        sx={{ width: { xs: '100%', sm: 300 } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => onChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />
      {value.trim() && (
        <Typography variant="caption" color="text.secondary">
          {shown} of {total} field{total === 1 ? '' : 's'}
        </Typography>
      )}
    </Stack>
  );
}
